import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { api, formatoEuros, type Foto, type Gasto as GastoServidor } from '../api';
import { Cabecera, AvisoError } from '../componentes';
import { useCola, useReferenciaResuelta } from '../hooks';
import { encolarFoto } from '../offline/cola';
import { prepararFoto } from '../offline/imagen';

interface FotoPintable {
  clave: string;
  nombre: string;
  url?: string;
  estado: 'subida' | 'pendiente' | 'subiendo' | 'error';
  error?: string;
}

export function Gasto({
  gastoRef,
  resumen,
  alVolver,
}: {
  /** Id de Gextia, o referencia local si el gasto aún no ha subido. */
  gastoRef: string | number;
  resumen: Pick<GastoServidor, 'description' | 'amount' | 'categoryName'>;
  alVolver: () => void;
}) {
  const [fotosServidor, setFotosServidor] = useState<Foto[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [procesando, setProcesando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);
  const cola = useCola();

  // Dos referencias, y la distinción importa:
  //  - `gastoRef` (la original) es con la que se guardaron las fotos locales,
  //    así que es la que hay que usar para filtrarlas y para encolar nuevas.
  //  - `refEfectiva` pasa a ser el id real en cuanto el gasto sube, y es la
  //    única con la que se puede preguntar a Gextia.
  const refEfectiva = useReferenciaResuelta(gastoRef);
  const idServidor = typeof refEfectiva === 'number' ? refEfectiva : null;

  const cargarFotos = useCallback(async () => {
    if (idServidor === null) return; // todavía no existe en Gextia
    try {
      setFotosServidor(await api.fotos(idServidor));
    } catch {
      // Sin conexión no pasa nada: abajo se ven igualmente las pendientes.
    }
  }, [idServidor]);

  useEffect(() => {
    void cargarFotos();
  }, [cargarFotos, cola.pendientes]);

  async function alElegirFotos(evento: ChangeEvent<HTMLInputElement>) {
    const archivos = Array.from(evento.target.files ?? []);
    // Permite volver a elegir el mismo archivo si el usuario repite la foto.
    evento.target.value = '';
    if (archivos.length === 0) return;

    setProcesando(true);
    setError(null);
    try {
      for (const archivo of archivos) {
        const preparada = await prepararFoto(archivo);
        await encolarFoto(gastoRef, preparada.blob, preparada.nombre);
      }
    } catch (fallo) {
      setError(fallo);
    } finally {
      setProcesando(false);
    }
  }

  async function borrarFoto(id: number) {
    setError(null);
    try {
      await api.borrarFoto(id);
      await cargarFotos();
    } catch (fallo) {
      setError(fallo);
    }
  }

  // Las locales se guardaron con la referencia ORIGINAL como padre, así que se
  // filtran por ella aunque el gasto ya tenga id real.
  const locales = cola.locales.filter(
    (l) => l.tipo === 'foto' && l.padre === String(gastoRef),
  );

  // Las miniaturas de lo pendiente se cachean por id local. Crearlas dentro del
  // render generaba un object URL nuevo en cada repintado —y la cola repinta a
  // cada cambio de estado—, lo que hacía parpadear las fotos y dejaba memoria
  // colgando.
  const miniaturas = useRef(new Map<string, string>());
  for (const local of locales) {
    if (!miniaturas.current.has(local.idLocal)) {
      const blob = local.datos.blob as Blob | undefined;
      if (blob) miniaturas.current.set(local.idLocal, URL.createObjectURL(blob));
    }
  }

  const clavesVivas = new Set(locales.map((l) => l.idLocal));
  useEffect(() => {
    const cache = miniaturas.current;
    for (const [clave, url] of cache) {
      if (!clavesVivas.has(clave)) {
        URL.revokeObjectURL(url);
        cache.delete(clave);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locales.length]);

  useEffect(() => {
    const cache = miniaturas.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  const fotos: FotoPintable[] = [
    ...fotosServidor.map((foto) => ({
      clave: `s${foto.id}`,
      nombre: foto.filename,
      // Ahora sí se ve el ticket guardado, en vez de un icono gris.
      url: `/api/fotos/${foto.id}/contenido`,
      estado: 'subida' as const,
    })),
    ...locales.map((local) => {
      const accion = cola.acciones.find((a) => a.produce === local.idLocal);
      return {
        clave: local.idLocal,
        nombre: local.datos.filename as string,
        url: miniaturas.current.get(local.idLocal),
        estado:
          accion?.irrecuperable === true
            ? ('error' as const)
            : accion?.estado === 'enviando'
              ? ('subiendo' as const)
              : ('pendiente' as const),
        error: accion?.ultimoError,
      };
    }),
  ];

  return (
    <>
      <Cabecera titulo="Tickets del gasto" alVolver={alVolver} />

      <div className="contenido">
        <div className="tarjeta">
          <div className="tarjeta-fila">
            <span className="tarjeta-titulo">{resumen.description}</span>
            <span className="importe">{formatoEuros(resumen.amount)}</span>
          </div>
          <div className="tarjeta-meta">
            <span className="etiqueta">{resumen.categoryName || 'Sin categoría'}</span>
          </div>
        </div>

        <AvisoError error={error} />

        {fotos.length === 0 ? (
          <div className="vacio">
            <p>Sin fotos todavía.</p>
            <p className="tarjeta-meta">Haz la foto del ticket antes de guardarlo.</p>
          </div>
        ) : (
          <div className="tira-fotos">
            {fotos.map((foto) => (
              <figure key={foto.clave} className={`foto foto-${foto.estado}`}>
                {foto.url ? (
                  <img src={foto.url} alt={foto.nombre} loading="lazy" />
                ) : (
                  <div className="foto-sin-vista" aria-hidden="true">
                    🧾
                  </div>
                )}
                <figcaption>
                  {foto.estado === 'subida' && <span className="marca ok">Guardada</span>}
                  {foto.estado === 'pendiente' && (
                    <span className="marca espera">En cola</span>
                  )}
                  {foto.estado === 'subiendo' && (
                    <span className="marca espera">Subiendo…</span>
                  )}
                  {foto.estado === 'error' && <span className="marca fallo">No subió</span>}
                </figcaption>
                {foto.estado === 'subida' && (
                  <button
                    type="button"
                    className="quitar-foto"
                    aria-label={`Quitar ${foto.nombre}`}
                    onClick={() => {
                      const id = Number(foto.clave.slice(1));
                      void borrarFoto(id);
                    }}
                  >
                    ×
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>

      <div className="acciones-abajo">
        <input
          ref={entrada}
          type="file"
          accept="image/*"
          // `environment` abre directamente la cámara trasera. `multiple` deja
          // encadenar varias fotos sin volver a la pantalla entre cada una.
          capture="environment"
          multiple
          hidden
          onChange={(e) => void alElegirFotos(e)}
        />
        <button
          type="button"
          className="boton ancho"
          onClick={() => entrada.current?.click()}
          disabled={procesando}
        >
          {procesando ? 'Preparando…' : 'Hacer foto del ticket'}
        </button>
        <button type="button" className="boton secundario ancho" onClick={alVolver}>
          Listo
        </button>
      </div>
    </>
  );
}
